import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import type { ProductImage } from '@optiflow/types';
import { cn } from '@/utils/cn';

const SIZES = {
  xs: 'size-8',
  sm: 'size-10',
  md: 'size-16',
  lg: 'size-24',
} as const;

interface ProductThumbProps {
  image: ProductImage | null;
  /** What the photograph shows, for screen readers: usually the product or variant name. */
  alt: string;
  size?: keyof typeof SIZES;
  className?: string;
}

/**
 * A product's thumbnail, or a placeholder when it has none.
 *
 * Always the small rendition, loaded lazily, so a long list never pulls a display-sized image. A URL
 * that has expired or no longer resolves falls back to the placeholder instead of a broken image.
 */
export function ProductThumb({ image, alt, size = 'sm', className }: ProductThumbProps) {
  const { t } = useTranslation();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const box = cn(
    SIZES[size],
    'bg-canvas border-line shrink-0 overflow-hidden rounded-md border',
    className,
  );

  if (!image || failedUrl === image.thumbnailUrl) {
    return (
      <span
        role="img"
        aria-label={t('catalog.media.noPhoto')}
        className={cn(box, 'text-muted grid place-items-center')}
      >
        <ImageOff className="size-1/2" aria-hidden />
      </span>
    );
  }

  return (
    <img
      src={image.thumbnailUrl}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={cn(box, 'object-cover')}
      onError={() => setFailedUrl(image.thumbnailUrl)}
    />
  );
}
